import { Helmet } from "react-helmet";
import { connect, useDispatch, useSelector } from "react-redux";
import { State } from "redux/store";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import {
  getIsCardslistLoading,
  getFilteredSimilarityClusters,
  getNavigationClusterLoader,
  isClusterMetadataSummaryEnabled,
  getTotalPagesForCluster,
  getIsImageUploading,
  getIsCustomMetadataEnabled,
  isDataMetadataSummaryEnabled,
  getFilteredNavigationCluster,
  getFilteredNavigationClusterTags,
  getSimilarityVertexCluster,
} from "redux/SingleDataset/selectors";
import {
  getImageData,
  getImageLabels,
  getShowLearnMoreModal,
  getShowRequestDemoModal,
} from "redux/Modals/selectors";
import {
  fetchDataset,
  fetchSimilarityData,
  setDatasetLabels,
  fetchDatasetLabels,
  fetchFilterConfig,
  fetchNavigationCluster,
  fetchClusterPagePagination,
  fetchDataPagePagination,
  fetchSimilarityThresholdsInfo,
  resetStore,
  setCardslistLoading,
  setNavigationContext,
  fetchClusterPageIssueTypes,
  fetchDatasetIssues,
  fetchDatasetTags,
  fetchMetadataSources,
  setScrollApplied,
  fetchSavedViews,
  setTreeFolder,
  fetchTreeFolder,
  fetchDatasetContextualLabels,
  fetchContextualDatasetTags,
  fetchCustomMetadata,
  setSimilarityVertex,
} from "redux/SingleDataset/actions";
import { fetchImageInfo, fetchImageUniquenessData } from "redux/Modals/actions";
import { fetchImageCustomMetadata } from "redux/SingleImage/actions";
import { fetchUserTags } from "redux/SingleDataset/actions";
import {
  CLUSTER_DETAILS_PAGE,
  DATA_PAGE,
  IMAGE_DETAILS_PAGE,
} from "helpers/constants/pages";
import { StatsProvider } from "contexts/StatsContext";
import { calculateTreeFolder } from "helpers/utility/utilities";
import {
  OBJECT_ID_QUERY_PARAMETER,
  FOLDER_PARAMETER,
  PAGE_PARAMETER,
  ENTITY_TYPE_PARAMETER,
  VERTEX_ID,
  VERTEX_TYPE,
} from "helpers/constants/miscellaneous";

import RequestDemoModal from "views/modals/RequestDemoModal";

import ContainerHeader from "views/components/ContainerHeader";
import HealthStatus from "views/components/HealthStatus";
import StatsFooter from "./StatsFooter";
import ImageView from "./ImageView";
import CardsView from "./CardsView";

import styles from "./style.module.scss";
import { FILE_FOLDER_FILTER } from "helpers/constants/filters";
import { ContactUsComponentType, EntityType, isValidEntityType } from "types";
import SignupTeaserModal from "views/modals/SignupTeaserModal";
import { LearnMoreModal } from "views/modals/LearnMoreModal";
import { convertURItoNumber } from "helpers/utility/utilities";
import useQueryParam from "hooks/useQueryParam";

const DataPage = ({
  clusters,
  isCardListLoading,
  navigationCluster,
  navigationClusterTags,
  navigationClusterLoader,
  imageData,
  labels,
  clusterMetadataSummaryEnabled,
  dataMetadataSummaryEnabled,
  isImageUploading,
  showRequestDemoModal,
  showLearnMoreModal,
}: DataPageProps) => {
  const [reviewCountText, setReviewCountText] = useState<string | null>(null);
  const queryParamsFilters = useAllQueryParamsFilters();
  const [commonDataFetched, setCommonDataFetched] = useState(false);
  const { datasetId, clusterId, imageId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const selectedLabelID = useQueryParam(OBJECT_ID_QUERY_PARAMETER);
  const entityParam = useQueryParam(ENTITY_TYPE_PARAMETER);
  const entityTypeValue: EntityType = isValidEntityType(entityParam)
    ? entityParam
    : "IMAGES";

  const isDataPage = !!datasetId && !imageId && !clusterId;
  const isClusterPage = !!datasetId && !!clusterId;
  const isImagePage = !!imageId;

  const dynamicPage = isImagePage
    ? IMAGE_DETAILS_PAGE
    : isClusterPage
      ? CLUSTER_DETAILS_PAGE
      : DATA_PAGE;

  const navigate = useNavigate();
  const singleDatasetState = useSelector((state: State) => state.singleDataset);
  const isCustomMetadataEnabled = useSelector(getIsCustomMetadataEnabled);
  const filterConfigAllows = (key: string, filterConfig: any) => {
    const singleFilter = filterConfig.find(
      (filter: any) => filter.feature_key === key
    );
    if (singleFilter) {
      if (singleFilter.feature_behavior.ANY !== "HIDE") {
        return true;
      }
    }
    return false;
  };

  const createParams = (searchParams: URLSearchParams, filterConfig: any) => {
    const params: { [key: string]: any } = {
      [FOLDER_PARAMETER]: null,
      [PAGE_PARAMETER]: null,
    };

    searchParams.forEach((value: string, key: string) => {
      switch (key) {
        case FOLDER_PARAMETER:
          if (filterConfigAllows(FILE_FOLDER_FILTER, filterConfig))
            params[FOLDER_PARAMETER] = decodeURIComponent(value);
          break;
        case PAGE_PARAMETER:
          if (convertURItoNumber(value)) {
            params[PAGE_PARAMETER] = convertURItoNumber(value);
          }
          break;
        default:
          break;
      }
    });

    return params;
  };
  const createStateFromParams = (params: any, state: any) => {
    Object.keys(params).forEach((key: string) => {
      switch (key) {
        case FOLDER_PARAMETER:
          //Ugly Hack:
          //refer to calculateTreeFolder in helpers/utility/utilities.ts
          if (params[key]) {
            const path =
              params[key].charAt(0) === "/" //if the first char is a slash remove it
                ? params[key].slice(1)
                : params[key];

            const pathValues = path.split("/");
            const pathHasChildren = pathValues.length > 1;
            if (pathHasChildren) {
              const treeFolder = calculateTreeFolder(path, state.treeFolder);
              dispatch(setTreeFolder(treeFolder));
            }
          }
          break;
        case PAGE_PARAMETER:
          // Navigation logic moved to separate effect to avoid circular dependency
          break;
      }
    });
  };

  const fetchCommonData = async () => {
    try {
      dispatch(setCardslistLoading(true));
      dispatch(setDatasetLabels([]));
      await Promise.all([
        // items in this list are blocking the page loading so the list should be the bare minimum that is required
        //  to render the page and not nothing else (additional settings can be loaded in the background)
        dispatch(fetchSimilarityThresholdsInfo(datasetId!) as any),
        dispatch(fetchFilterConfig(datasetId) as any),
      ]);
      setCommonDataFetched(true);
    } catch (error) {
      console.log(error);
    }
  };
  const fetchForDataPage = (datasetID: string) => {
    dispatch(setCardslistLoading(true));
    dispatch(fetchDataset(datasetId) as any);
    dispatch(
      fetchDatasetContextualLabels(datasetId, queryParamsFilters) as any
    );
    dispatch(fetchDataPagePagination(datasetID, queryParamsFilters) as any);
    dispatch(fetchSimilarityData(datasetId, queryParamsFilters) as any);
    dispatch(fetchDatasetIssues(datasetId, queryParamsFilters) as any);
  };
  const fetchForClusterPage = (datasetID: string, clusterID: string) => {
    dispatch(fetchDataset(datasetId, clusterId) as any);
    dispatch(
      fetchDatasetContextualLabels(
        datasetId,
        queryParamsFilters,
        clusterId
      ) as any
    );
    dispatch(fetchUserTags(datasetId, queryParamsFilters, clusterId) as any);
    dispatch(
      fetchClusterPageIssueTypes(
        datasetId,
        clusterId,
        queryParamsFilters
      ) as any
    );
    dispatch(
      fetchClusterPagePagination(
        datasetID,
        clusterID,
        queryParamsFilters
      ) as any
    );
    dispatch(
      fetchNavigationCluster(datasetID, clusterID, queryParamsFilters) as any
    );
  };
  const fetchForImagePage = (imageID: string) => {
    dispatch(fetchDataset(datasetId) as any);
    dispatch(fetchImageInfo(imageID, selectedLabelID) as any);
    dispatch(fetchImageUniquenessData(imageID) as any);
  };

  useEffect(() => {
    fetchCommonData();
    //eslint-disable-next-line
  }, []);

  // URL initialization (runs once to set page=1 if missing)
  useEffect(() => {
    if (!searchParams.has(PAGE_PARAMETER) && !isImagePage) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(PAGE_PARAMETER, "1");

      let path = `${location.pathname}?${newSearchParams.toString()}`;
      navigate(path, { replace: true });
    }
    //eslint-disable-next-line
  }, [location.pathname, isImagePage, navigate]);

  // Main data fetching (runs when page parameter exists)
  useEffect(() => {
    if (commonDataFetched) {
      dispatch(fetchDatasetTags(datasetId, entityTypeValue) as any);
      dispatch(fetchSavedViews(datasetId) as any);
      dispatch(fetchDatasetLabels(datasetId, entityTypeValue) as any);
      dispatch(fetchTreeFolder(datasetId) as any);
      dispatch(fetchMetadataSources(datasetId!, entityTypeValue) as any);
      dispatch(fetchCustomMetadata(datasetId!, entityTypeValue) as any);
    }
  }, [commonDataFetched, datasetId, entityTypeValue, dispatch]);

  useEffect(() => {
    if (
      commonDataFetched &&
      (searchParams.has(PAGE_PARAMETER) || isImagePage)
    ) {
      dispatch(
        fetchContextualDatasetTags(datasetId, queryParamsFilters) as any
      );
      const params = createParams(
        searchParams,
        singleDatasetState.filterConfig
      );
      createStateFromParams(params, singleDatasetState);

      if (isImagePage) {
        fetchForImagePage(imageId);
        dispatch(setNavigationContext(IMAGE_DETAILS_PAGE) as any);
        if (isCustomMetadataEnabled) {
          dispatch(fetchImageCustomMetadata(datasetId!, imageId!) as any);
        }
      } else if (params[PAGE_PARAMETER] || params[VERTEX_ID]) {
        if (isClusterPage) {
          fetchForClusterPage(datasetId, clusterId);
          dispatch(setNavigationContext(CLUSTER_DETAILS_PAGE) as any);
        } else if (isDataPage) {
          fetchForDataPage(datasetId);
          dispatch(setNavigationContext(DATA_PAGE) as any);
        }
      }
    }
    //eslint-disable-next-line
  }, [commonDataFetched, datasetId, clusterId, imageId, searchParams]);

  useEffect(() => {
    const handlePopState = () => {
      //applies scroll on back navigation from browser
      dispatch(setScrollApplied(false));
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      dispatch(resetStore());
    };
  }, [dispatch]);

  const currentVertexCluster = useSelector(getSimilarityVertexCluster);
  useEffect(() => {
    const hasVertexParams =
      queryParamsFilters[VERTEX_ID] || queryParamsFilters[VERTEX_TYPE];

    if (!hasVertexParams && currentVertexCluster) {
      // Clear vertex state when vertex parameters are not present in URL but state still exists
      // This handles browser navigation where URL changes but Redux state doesn't
      dispatch(setSimilarityVertex(null));
    }
  }, [queryParamsFilters, currentVertexCluster, dispatch]);

  return (
    <>
      <Helmet>
        <title>Dataset Exploration</title>
      </Helmet>
      <div className={styles.headerContainer}>
        <ContainerHeader
          dynamicPage={dynamicPage}
          isCardListLoading={isCardListLoading}
          navigationClusterLoader={navigationClusterLoader}
        />
        <HealthStatus />
      </div>

      {showRequestDemoModal.flag && (
        <RequestDemoModal calledFrom={showRequestDemoModal.calledFrom} />
      )}
      {showLearnMoreModal && <LearnMoreModal />}
      {dynamicPage === IMAGE_DETAILS_PAGE ? (
        <ImageView labels={labels} imageData={imageData} />
      ) : (
        <>
          <StatsProvider>
            <CardsView
              clusters={clusters}
              dynamicPage={dynamicPage}
              navigationCluster={navigationCluster}
              navigationClusterTags={navigationClusterTags}
              isCardListLoading={isCardListLoading}
              navigationClusterLoader={navigationClusterLoader}
              metadataSummaryEnabled={
                isDataPage
                  ? dataMetadataSummaryEnabled
                  : clusterMetadataSummaryEnabled
              }
              isImageUploading={isImageUploading}
              setReviewCountText={setReviewCountText}
            />
          </StatsProvider>
          <StatsFooter reviewCountText={reviewCountText} />
        </>
      )}
      <SignupTeaserModal />
    </>
  );
};

const mapStatesToProps = (state: State) => {
  return {
    isCardListLoading: getIsCardslistLoading(state),
    clusters: getFilteredSimilarityClusters(state),
    navigationCluster: getFilteredNavigationCluster(state),
    navigationClusterTags: getFilteredNavigationClusterTags(state),
    navigationClusterLoader:
      getNavigationClusterLoader(state) || !getTotalPagesForCluster(state),
    imageData: getImageData(state),
    labels: getImageLabels(state),
    clusterMetadataSummaryEnabled: isClusterMetadataSummaryEnabled(state),
    dataMetadataSummaryEnabled: isDataMetadataSummaryEnabled(state),
    isImageUploading: getIsImageUploading(state),
    showRequestDemoModal: getShowRequestDemoModal(state),
    showLearnMoreModal: getShowLearnMoreModal(state),
  };
};

export default connect(mapStatesToProps)(DataPage);

interface DataPageProps {
  labels: any;
  clusters: any;
  imageData: any;
  navigationCluster: any;
  navigationClusterTags: any;
  isImageUploading: boolean;
  isCardListLoading: boolean;
  navigationClusterLoader: boolean;
  clusterMetadataSummaryEnabled: boolean;
  dataMetadataSummaryEnabled: boolean;
  showRequestDemoModal: { flag: boolean; calledFrom: ContactUsComponentType };
  showLearnMoreModal: boolean;
}
